import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const ProductModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_recurring: false,
    recurring_interval: 'month',
    active: true,
    polar_id: '',
    support_credits: 0,
    custom_credits: 0,
    metadata: {
      type: 'one-time',
      price_amount: 0
    }
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Pre-populate form if product is provided (edit mode)
    if (product) {
      // Parse metadata if it's a string
      let metadata = product.metadata;
      if (metadata && typeof metadata === 'string') {
        try {
          metadata = JSON.parse(metadata);
        } catch (e) {
          console.error('Failed to parse product metadata in modal:', e);
          metadata = {};
        }
      }
      
      setFormData({
        name: product.name || '',
        description: product.description || '',
        is_recurring: product.is_recurring === 1,
        recurring_interval: product.recurring_interval || 'month',
        active: product.active === 1,
        polar_id: product.polar_id || '',
        support_credits: product.support_credits || 0,
        custom_credits: product.custom_credits || 0,
        metadata: {
          type: metadata?.type || 'one-time',
          price_amount: metadata?.price_amount || 0
        }
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'price_amount') {
      // Convert dollars to cents
      const cents = Math.round(parseFloat(value) * 100);
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          price_amount: cents
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (formData.metadata.price_amount <= 0) {
      newErrors.price_amount = 'Price must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert form data to match API structure
      const productData = {
        ...formData,
        is_recurring: formData.is_recurring ? 1 : 0,
        active: formData.active ? 1 : 0,
        metadata: {
          ...formData.metadata,
          type: formData.is_recurring ? 'subscription' : 'one-time'
        }
      };
      
      // Ensure metadata is stringified consistently for the API
      // Some APIs expect metadata as a JSON string
      if (productData.metadata && typeof productData.metadata === 'object') {
        // Keep the object format since the API likely expects an object
        // The API or backend should handle serialization if needed
      }
      
      await onSave(productData);
    } catch (err) {
      console.error('Error saving product:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h2 className="text-xl font-semibold">
            {product ? 'Edit Product' : 'Create New Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            {/* Basic Information */}
            <div>
              <h3 className="font-medium text-lg mb-2">Basic Information</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter product name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows="3"
                    placeholder="Enter product description"
                  ></textarea>
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Pricing Information */}
            <div>
              <h3 className="font-medium text-lg mb-2">Pricing Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        name="is_recurring"
                        checked={formData.is_recurring}
                        onChange={handleChange}
                        className="form-checkbox h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Recurring Subscription</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (in dollars)
                  </label>
                  <input
                    type="number"
                    name="price_amount"
                    value={formData.metadata && formData.metadata.price_amount ? 
                      (formData.metadata.price_amount / 100).toFixed(2) : "0.00"}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.price_amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {errors.price_amount && (
                    <p className="mt-1 text-sm text-red-500">{errors.price_amount}</p>
                  )}
                </div>
                
                {formData.is_recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Interval
                    </label>
                    <select
                      name="recurring_interval"
                      value={formData.recurring_interval}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                      <option value="week">Weekly</option>
                      <option value="day">Daily</option>
                    </select>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    id="active"
                    checked={formData.active}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Active Product
                  </label>
                </div>
              </div>
            </div>
            
            {/* Polar.sh Integration */}
            <div>
              <h3 className="font-medium text-lg mb-2">Polar.sh Integration</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Polar Product ID
                  </label>
                  <input
                    type="text"
                    name="polar_id"
                    value={formData.polar_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter Polar.sh product ID"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This field is used to map products between your system and Polar.sh. 
                Leave blank if not applicable.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Support Credits
              </label>
              <input
                type="number"
                name="support_credits"
                value={formData.support_credits}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Development Credits
              </label>
              <input
                type="number"
                name="custom_credits"
                value={formData.custom_credits}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="0"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;
